import { Box, Button, ButtonGroup, Dialog, Fab, Paper, TableContainer } from "@mui/material"
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import { CompanyMapping, loadCompanyMappingsByCompanyId, loadSuppliers } from "./CompanyMappingsApi"
import { CreateCompanyMappingDialog } from "./CreateCompanyMappingDialog"
import { DeleteCompanyMappingDialog } from "./DeleteCompanyMappingDialog"
import { Company, loadCompanies } from "../companiesSlice"
import { Supplier } from "../../suppliers/suppliersSlice"
import { FindPotentialCompanyMappingDialog } from "./FindPotentialCompanyMappingDialog"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"


interface CompanyMappingsProps {
    companyId: string;
}

type DialogState =
    | { type: "none" }
    | { type: "create", companyMapping: CompanyMapping | undefined }
    | { type: "delete", companyMappingId: string }
    | { type: "findPotentialMapping", companyId: string }

export const CompanyMappingsForCompany: React.FC<CompanyMappingsProps> = ({ companyId }) => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [companyMappings, setCompanyMappings] = useState<CompanyMapping[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [companies, setCompanies] = useState<Company[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);

    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCompanyMappingsByCompanyId(companyId))
            .then(unwrapResult)
            .then(setCompanyMappings)
            .catch((error) => console.error(error))
            .finally(() => setIsLoading(false));
        dispatch(loadCompanies())
            .then(unwrapResult)
            .then(setCompanies)
            .catch((error) => console.error(error))
        dispatch(loadSuppliers())
            .then(unwrapResult)
            .then(setSuppliers)
            .catch((error) => console.error(error))
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((companyMapping: CompanyMapping | CompanyMapping[] | string) => {
            setter({ type: "none" })
            setCompanyMappings(
                produce((draft) => {
                    if (draft !== undefined) {
                        if(Array.isArray(companyMapping)) {
                            draft.push(...companyMapping);
                        } else if(typeof(companyMapping) === "string") {
                            const existingIndex = draft.findIndex((group) => group.id === companyMapping);

                            if (existingIndex !== -1) {
                                draft.splice(existingIndex, 1);
                            }
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === companyMapping.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = companyMapping;
                            } else {
                                draft.push(companyMapping);
                            }
                        }
                    }
                })
            )
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "create":
                return <CreateCompanyMappingDialog
                    companyId={companyId}
                    companies={companies}
                    suppliers={suppliers}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
            case "delete":
                return <DeleteCompanyMappingDialog
                    companyMappingId={state.companyMappingId}
                    onDeleteSuccess={onSuccess}
                    onCancel={onCancel}
                />
            case "findPotentialMapping":
                return <FindPotentialCompanyMappingDialog
                    companyId={companyId}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const handleAddMappingClick = useCallback(() => {
        setDialog({
            type: "create",
            companyMapping: undefined
        })
    }, [setDialog])

    const handleDeleteMappingClick = useCallback((companyMappingId: string) => {
        setDialog({
            type: "delete",
            companyMappingId
        })
    }, [setDialog])

    const handleFindPotentialMappingClick = useCallback(() => {
        setDialog({
            type: "findPotentialMapping",
            companyId
        })
    }, [setDialog, companyId])

    const columns: GridColDef<CompanyMapping, any, any>[] = [
        {
            field: 'no',
            headerName: l10n.getString("lp", null, "_Lp.")
        },
        {
            field: 'supplier.name',
            headerName: l10n.getString("company-supplier-mappings-supplier-name", null, "_Nazwa dostawcy"),
            flex: 1,
            valueGetter: (_, row) => row.supplier.name
        },
        {
            field: 'supplier.shortName',
            headerName: l10n.getString("company-supplier-mappings-supplier-short-name", null, "_Skrócona nazwa dostawcy"),
            flex: 1,
            valueGetter: (_, row) => row.supplier.shortName
        },
        {
            field: 'supplier.email',
            headerName: l10n.getString("company-supplier-mappings-supplier-email", null, "_E-mail dostawcy"),
            flex: 1,
            valueGetter: (_, row) => row.supplier.email
        },
        {
            field: 'supplier.company',
            headerName: l10n.getString("company-supplier-mappings-contact-owner", null, "_Właściciel kontaktu (firma zarejestrowana)"),
            flex: 1,
            valueGetter: (_, row) => {
                const company = companies.find(f => f.id === String(row.supplier.companyId));
                return company ? company.name : "";
            }
        },
        {
            field: 'actions',
            type: 'actions',
            width: 250,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleDeleteMappingClick(params.row.id)}
                        >
                            <Localized id="delete">
                                <span>_Usuń</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 415px)' }}
                    rows={companyMappings.map((row, index) => ({ ...row, no: index + 1 }))}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'supplier.name', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                    onClick={() => handleAddMappingClick()} color="secondary" size="small" >
                        <AddIcon />
                    </Fab>
                    <Button sx={{ ml: 2 }} onClick={() => handleFindPotentialMappingClick()}>
                        <Localized id="company-supplier-mappings-potential">
                            <span>_Wyszukaj potencjalne mapowania</span>
                        </Localized>
                    </Button>
                </Box>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth={dialog.type === "create" || dialog.type === "findPotentialMapping" ? "lg" : "sm"} keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
